const API_BASE = import.meta.env.VITE_TSRA_API_BASE || "http://localhost:3000/api";

function getTokens() {
  try {
    return JSON.parse(localStorage.getItem("tsra_tokens") || "null");
  } catch {
    return null;
  }
}

function setTokens(tokens) {
  if (tokens) localStorage.setItem("tsra_tokens", JSON.stringify(tokens));
  else localStorage.removeItem("tsra_tokens");
}

async function refreshTokens() {
  const current = getTokens();
  if (!current?.refreshToken) return null;
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) {
    setTokens(null);
    return null;
  }
  const data = await res.json();
  setTokens(data);
  return data;
}

async function request(path, { method = "GET", body, skipAuth = false, _retried = false } = {}) {
  const tokens = getTokens();
  const headers = { "Content-Type": "application/json" };
  if (!skipAuth && tokens?.accessToken) {
    headers.Authorization = `Bearer ${tokens.accessToken}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !skipAuth && !_retried) {
    const refreshed = await refreshTokens();
    if (refreshed) return request(path, { method, body, skipAuth, _retried: true });
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    // no body
  }

  if (!res.ok) {
    const message = data?.message || data?.eroare || `Eroare ${res.status}`;
    const err = new Error(Array.isArray(message) ? message.join(" ") : message);
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  login: async (email, password) => {
    const data = await request("/auth/login", { method: "POST", body: { email, password }, skipAuth: true });
    setTokens(data);
    return data;
  },
  logout: async () => {
    const tokens = getTokens();
    if (tokens?.refreshToken) {
      await request("/auth/logout", { method: "POST", body: { refreshToken: tokens.refreshToken } }).catch(() => {});
    }
    setTokens(null);
  },
  getStoredUser: () => getTokens()?.user ?? null,
  isAuthenticated: () => !!getTokens()?.accessToken,

  clients: {
    list: () => request("/clients"),
    create: (data) => request("/clients", { method: "POST", body: data }),
  },
  users: {
    list: () => request("/users"),
    create: (data) => request("/users", { method: "POST", body: data }),
  },
  evaluari: {
    list: (clientId) => request(`/evaluari${clientId ? `?clientId=${clientId}` : ""}`),
    get: (id) => request(`/evaluari/${id}`),
    create: (data) => request("/evaluari", { method: "POST", body: data }),
    update: (id, data) => request(`/evaluari/${id}`, { method: "PATCH", body: data }),
    populeazaImplicit: (id) => request(`/evaluari/${id}/populeaza-implicit`, { method: "POST" }),
    exportProiect: (id) => request(`/evaluari/${id}/export-proiect`),
  },
  active: {
    create: (data) => request("/active", { method: "POST", body: data }),
    update: (id, data) => request(`/active/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/active/${id}`, { method: "DELETE" }),
  },
  amenintari: {
    create: (data) => request("/amenintari", { method: "POST", body: data }),
    update: (id, data) => request(`/amenintari/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/amenintari/${id}`, { method: "DELETE" }),
  },
  hazarduri: {
    create: (data) => request("/hazarduri", { method: "POST", body: data }),
    update: (id, data) => request(`/hazarduri/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/hazarduri/${id}`, { method: "DELETE" }),
  },
  riscuri: {
    create: (data) => request("/risc-registru", { method: "POST", body: data }),
    update: (id, data) => request(`/risc-registru/${id}`, { method: "PATCH", body: data }),
    recalculeaza: (id) => request(`/risc-registru/${id}/recalculeaza`, { method: "POST" }),
    remove: (id) => request(`/risc-registru/${id}`, { method: "DELETE" }),
  },
  tratamente: {
    create: (data) => request("/tratamente", { method: "POST", body: data }),
    update: (id, data) => request(`/tratamente/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/tratamente/${id}`, { method: "DELETE" }),
  },
  controale: {
    create: (data) => request("/controale", { method: "POST", body: data }),
    update: (id, data) => request(`/controale/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/controale/${id}`, { method: "DELETE" }),
  },
  bowTie: {
    create: (data) => request("/bow-tie", { method: "POST", body: data }),
    update: (id, data) => request(`/bow-tie/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/bow-tie/${id}`, { method: "DELETE" }),
  },
  angajament: {
    create: (data) => request("/angajament", { method: "POST", body: data }),
    update: (id, data) => request(`/angajament/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/angajament/${id}`, { method: "DELETE" }),
  },
  raport: {
    salveazaSectiune: (evaluareId, etapa, continut) =>
      request(`/raport/${evaluareId}/${etapa}`, { method: "PATCH", body: { continut } }),
  },
  easi: {
    creeazaScenariu: (data) => request("/easi/scenarii", { method: "POST", body: data }),
    actualizeazaScenariu: (id, data) => request(`/easi/scenarii/${id}`, { method: "PATCH", body: data }),
    stergeScenariu: (id) => request(`/easi/scenarii/${id}`, { method: "DELETE" }),
    creeazaTraseu: (data) => request("/easi/trasee", { method: "POST", body: data }),
    actualizeazaTraseu: (id, data) => request(`/easi/trasee/${id}`, { method: "PATCH", body: data }),
    stergeTraseu: (id) => request(`/easi/trasee/${id}`, { method: "DELETE" }),
    creeazaPas: (data) => request("/easi/pasi", { method: "POST", body: data }),
    actualizeazaPas: (id, data) => request(`/easi/pasi/${id}`, { method: "PATCH", body: data }),
    stergePas: (id) => request(`/easi/pasi/${id}`, { method: "DELETE" }),
  },
  catalog: {
    get: () => request("/catalog"),
    adaugaPersonalizat: (data) => request("/catalog/personalizat", { method: "POST", body: data }),
  },
  costuri: {
    create: (data) => request("/costuri", { method: "POST", body: data }),
    update: (id, data) => request(`/costuri/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/costuri/${id}`, { method: "DELETE" }),
  },
  evidente: {
    create: (data) => request("/evidente", { method: "POST", body: data }),
    update: (id, data) => request(`/evidente/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/evidente/${id}`, { method: "DELETE" }),
  },
  supraveghere: {
    create: (data) => request("/supraveghere", { method: "POST", body: data }),
    update: (id, data) => request(`/supraveghere/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/supraveghere/${id}`, { method: "DELETE" }),
  },
  elementeLocatie: {
    create: (data) => request("/element-locatie", { method: "POST", body: data }),
    update: (id, data) => request(`/element-locatie/${id}`, { method: "PATCH", body: data }),
    remove: (id) => request(`/element-locatie/${id}`, { method: "DELETE" }),
  },
};
